"use client";

import { useCallback, useState, useEffect } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

export function useDoorKnockingPreference() {
  const { user, refetchUser } = useUserContext();

  const contextValue = user?.user?.appPreferences?.doorKnockingEnabled ?? true;
  const [localEnabled, setLocalEnabled] = useState(contextValue);

  // Sync with context when it changes (e.g., after page navigation)
  useEffect(() => {
    setLocalEnabled(contextValue);
  }, [contextValue]);

  const setDoorKnockingEnabled = useCallback(async (value: boolean) => {
    // Immediate local state update (no waiting for context propagation)
    setLocalEnabled(value);

    // Update user context
    refetchUser((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        appPreferences: {
          ...(prev.user.appPreferences || {}),
          doorKnockingEnabled: value,
        },
      },
    }));

    try {
      await api.patch({
        url: getApiUrl("/users/profile/app-preferences"),
        body: { doorKnockingEnabled: value },
        auth: true,
      });
    } catch (error) {
      console.error("Failed to save door knocking preference:", error);
      // Revert on failure
      setLocalEnabled(!value);
      refetchUser((prev) => ({
        ...prev,
        user: {
          ...prev.user,
          appPreferences: {
            ...(prev.user.appPreferences || {}),
            doorKnockingEnabled: !value,
          },
        },
      }));
    }
  }, [refetchUser]);

  return { doorKnockingEnabled: localEnabled, setDoorKnockingEnabled };
}
